import React from "react";
import { Link } from "react-router-dom";

const cream = "#F5F0E8";
const purple = "#8B5CF6";
const darkBg = "#1a1025";
const mutedCream = "rgba(245,240,232,0.6)";

const DOWNLOAD_URL = "https://www.plexys.co/downloads/LabManager.dmg";

const features = [
  { emoji: "🔬", title: "Projects", desc: "Track research from idea to publication with Kanban and list views" },
  { emoji: "👥", title: "Personnel", desc: "Manage lab members, roles, and workload" },
  { emoji: "📝", title: "Working Manuscripts", desc: "Pipeline from draft to publication with author ordering" },
  { emoji: "📋", title: "IRB Protocols", desc: "Track submissions with expiration alerts" },
  { emoji: "💰", title: "Grants", desc: "Manage funding applications with deadline tracking" },
  { emoji: "🤝", title: "Meetings", desc: "Schedule meetings, track action items" },
  { emoji: "🔍", title: "Global Search", desc: "Find anything instantly with ⌘K" },
  { emoji: "📚", title: "Relevant Citations", desc: "Organize reference papers per project" },
  { emoji: "🔄", title: "Team Sync", desc: "Real-time collaboration powered by Supabase (Pro)" },
  { emoji: "🔒", title: "Local-First", desc: "Your data stays on your machine. Works offline." },
];

const steps = [
  "Download LabManager for macOS",
  "Set up your lab — add projects, team members, and manuscripts",
  "Invite your team (Pro) — share an invite code, sync in real-time",
];

export default function LabManagerLanding() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: darkBg, color: cream }}>
      {/* Nav */}
      <nav className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <span className="text-2xl">🔬</span>
          <span className="text-xl font-bold" style={{ color: cream }}>LabManager</span>
        </Link>
        <a href={DOWNLOAD_URL} className="text-sm font-medium px-5 py-2 rounded-full" style={{ backgroundColor: purple, color: "#fff" }}>
          Download
        </a>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 py-16 md:py-24 text-center">
        <div className="text-6xl mb-8">🔬</div>
        <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ color: cream }}>Your Research Lab, Organized.</h1>
        <p className="text-xl md:text-2xl mb-4 max-w-3xl mx-auto" style={{ color: mutedCream }}>
          A local-first desktop app for managing research projects, personnel, manuscripts, grants, and IRB protocols. Built for academic labs.
        </p>
        <p className="text-sm mb-10" style={{ color: mutedCream }}>Free for individual use • $50/mo for team sync</p>
        <a href={DOWNLOAD_URL} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-white font-semibold text-lg" style={{ backgroundColor: purple }}>
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
          Download for macOS
        </a>
      </section>

      {/* Features */}
      <section className="py-16" style={{ backgroundColor: "rgba(139,92,246,0.04)" }}>
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4" style={{ color: cream }}>Everything Your Lab Needs</h2>
          <p className="text-center mb-12" style={{ color: mutedCream }}>Purpose-built for academic research workflows</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {features.map((f, i) => (
              <div key={i} className="p-5 rounded-2xl" style={{ backgroundColor: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.12)" }}>
                <div className="text-2xl mb-2">{f.emoji}</div>
                <p className="font-semibold text-sm mb-1" style={{ color: cream }}>{f.title}</p>
                <p className="text-xs" style={{ color: mutedCream }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-3xl mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: cream }}>How It Works</h2>
        <div className="space-y-0">
          {steps.map((text, i) => (
            <div key={i} className="flex items-center gap-6 py-6" style={{ borderBottom: i < steps.length - 1 ? "1px solid rgba(139,92,246,0.12)" : "none" }}>
              <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold" style={{ backgroundColor: purple }}>
                {i + 1}
              </div>
              <p className="text-xl md:text-2xl font-medium" style={{ color: cream }}>{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-4 py-16 text-center" style={{ backgroundColor: "rgba(139,92,246,0.04)" }}>
        <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: cream }}>Simple Pricing</h2>
        <p className="text-lg mb-8" style={{ color: mutedCream }}>Free forever for individual researchers.</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 max-w-2xl mx-auto">
          <div className="w-full sm:w-1/2 p-8 rounded-2xl" style={{ backgroundColor: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.2)" }}>
            <p className="text-sm mb-1" style={{ color: mutedCream }}>Free</p>
            <p className="text-3xl font-bold mb-3" style={{ color: cream }}>$0</p>
            <p className="text-sm" style={{ color: mutedCream }}>Full app for individual researchers. Local data, offline, no limits.</p>
          </div>
          <div className="w-full sm:w-1/2 p-8 rounded-2xl relative" style={{ backgroundColor: "rgba(139,92,246,0.1)", border: `2px solid ${purple}` }}>
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-semibold px-3 py-1 rounded-full text-white" style={{ backgroundColor: purple }}>Team</span>
            <p className="text-sm mb-1" style={{ color: mutedCream }}>Team</p>
            <p className="text-3xl font-bold mb-3" style={{ color: cream }}>$50<span className="text-lg font-normal">/mo</span></p>
            <p className="text-sm" style={{ color: mutedCream }}>Multi-user sync, real-time collaboration, team management, admin controls.</p>
          </div>
        </div>
        <div className="mt-10">
          <a href={DOWNLOAD_URL} className="inline-block px-8 py-4 rounded-xl text-white font-semibold text-lg" style={{ backgroundColor: purple }}>
            Download LabManager Free
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t" style={{ borderColor: "rgba(139,92,246,0.15)" }}>
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-sm" style={{ color: mutedCream }}>
          <p>&copy; {new Date().getFullYear()} Plexys. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/labmanager/privacy" className="hover:opacity-100 transition">Privacy Policy</Link>
            <Link to="/labmanager/terms" className="hover:opacity-100 transition">Terms of Service</Link>
            <Link to="/" className="hover:opacity-100 transition">A Plexys Product</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
